// SA Feature Registry — manages global beta/internal feature flags.
// Each entry has a status (dev / internal_only / beta / ga) and an allowlist
// of org_ids that get early access. Frontend `<BetaGate>` reads
// `subscription.capabilities._beta_features` and decides what to render.
import React, { useEffect, useState, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import SALayout from './SALayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Beaker, Plus, Save, Trash2, Loader2, X, Check, Globe, EyeOff, Users2, Code2,
} from 'lucide-react';

const STATUS_DEFS = [
  { value: 'dev',           label: 'Dev only',       icon: Code2,   tone: 'bg-slate-100 text-slate-600',     desc: 'Hidden everywhere — even from allowlist.' },
  { value: 'internal_only', label: 'Internal only',  icon: EyeOff,  tone: 'bg-purple-100 text-purple-700',   desc: 'Only orgs in allowlist see this. Internal team / dogfooding.' },
  { value: 'beta',          label: 'Beta',           icon: Beaker,  tone: 'bg-amber-100 text-amber-700',     desc: 'Allowlisted customers get early access. Show 🧪 badge.' },
  { value: 'ga',            label: 'GA — everyone',  icon: Globe,   tone: 'bg-emerald-100 text-emerald-700', desc: 'Generally available — visible to all eligible orgs.' },
];

function StatusPill({ status }) {
  const def = STATUS_DEFS.find(s => s.value === status) || STATUS_DEFS[0];
  const Icon = def.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${def.tone}`}><Icon size={10} /> {def.label}</span>;
}

export default function SAFeatureRegistry() {
  const { apiFetch } = useSuperAdmin();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ key: '', label: '', description: '', status: 'internal_only' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reg, allOrgs] = await Promise.all([
        apiFetch('/feature-registry').then(r => r.ok ? r.json() : { items: [] }),
        apiFetch('/organizations').then(r => r.ok ? r.json() : []),
      ]);
      setItems(reg.items || []);
      setOrgs(allOrgs || []);
    } finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.key || !/^[a-z0-9_]+$/.test(draft.key)) {
      toast.error('Key must be lowercase letters, numbers, or underscores only');
      return;
    }
    const res = await apiFetch('/feature-registry', { method: 'POST', body: JSON.stringify(draft) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data.detail || 'Create failed'); return; }
    toast.success(`Feature "${draft.key}" registered`);
    setDraft({ key: '', label: '', description: '', status: 'internal_only' });
    setCreating(false);
    load();
  };

  return (
    <SALayout>
      <div className="space-y-4" data-testid="sa-feature-registry">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Beaker size={18} /> Feature Registry</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">Roll new features out gradually — internal team first, then allowlisted customers, then everyone.</p>
          </div>
          <Button size="sm" onClick={() => setCreating(true)} disabled={creating} data-testid="sa-fr-new-btn">
            <Plus size={14} className="mr-1" /> Register feature
          </Button>
        </div>

        {creating && (
          <Card className="border-slate-200/60" data-testid="sa-fr-new-card">
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm">Register a new feature</CardTitle>
              <button onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-slate-500">Key (a-z, 0-9, _)</Label>
                <Input value={draft.key} onChange={e => setDraft(d => ({ ...d, key: e.target.value.toLowerCase() }))} placeholder="github_pr_linking" className="mt-1 h-8 text-sm font-mono" data-testid="sa-fr-new-key" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Display label</Label>
                <Input value={draft.label} onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} placeholder="GitHub PR Linking" className="mt-1 h-8 text-sm" data-testid="sa-fr-new-label" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] text-slate-500">Description (optional)</Label>
                <Input value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} placeholder="Auto-link tasks to GitHub PRs and update status on merge" className="mt-1 h-8 text-sm" data-testid="sa-fr-new-desc" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] text-slate-500">Initial status</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-1">
                  {STATUS_DEFS.map(s => (
                    <button
                      key={s.value} type="button"
                      onClick={() => setDraft(d => ({ ...d, status: s.value }))}
                      className={`px-2.5 py-2 rounded-lg border text-[11px] font-semibold flex items-center gap-1.5 transition ${draft.status === s.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 hover:bg-slate-50'}`}
                      data-testid={`sa-fr-new-status-${s.value}`}
                    >
                      <s.icon size={12} /> {s.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">{STATUS_DEFS.find(s => s.value === draft.status)?.desc}</p>
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={() => setCreating(false)} className="h-8 text-[12px]">Cancel</Button>
                <Button size="sm" onClick={create} className="h-8 text-[12px] bg-slate-900 hover:bg-slate-800" data-testid="sa-fr-new-save"><Save size={12} className="mr-1" /> Register</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <Card className="border-slate-200/60">
            <CardContent className="py-16 text-center">
              <Beaker className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No features registered yet.</p>
              <p className="text-[11px] text-slate-400 mt-1">Use this to gate in-development features behind specific orgs before rolling them out broadly.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map(item => <FeatureRow key={item.key} item={item} orgs={orgs} apiFetch={apiFetch} onChanged={load} />)}
          </div>
        )}
      </div>
    </SALayout>
  );
}

function FeatureRow({ item, orgs, apiFetch, onChanged }) {
  const [status, setStatus] = useState(item.status);
  const [allowlist, setAllowlist] = useState(item.allowlist_org_ids || []);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = status !== item.status || JSON.stringify([...allowlist].sort()) !== JSON.stringify([...(item.allowlist_org_ids || [])].sort());

  const save = async () => {
    setBusy(true);
    try {
      const res = await apiFetch(`/feature-registry/${item.key}`, {
        method: 'PUT',
        body: JSON.stringify({ status, allowlist_org_ids: allowlist }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Save failed'); return; }
      toast.success(`"${item.key}" updated`);
      onChanged();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete feature "${item.key}"? This will hide it from everyone immediately.`)) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/feature-registry/${item.key}`, { method: 'DELETE' });
      if (!res.ok) { toast.error('Delete failed'); return; }
      toast.success(`"${item.key}" removed`);
      onChanged();
    } finally { setBusy(false); }
  };

  const filteredOrgs = orgs.filter(o => !search || o.name.toLowerCase().includes(search.toLowerCase()));
  const allowlistSet = new Set(allowlist);
  const showAllowlist = status === 'beta' || status === 'internal_only';

  return (
    <Card className="border-slate-200/60" data-testid={`sa-fr-row-${item.key}`}>
      <CardContent className="pt-5 pb-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[12px] font-bold text-slate-900">{item.key}</span>
              <StatusPill status={item.status} />
              {item.label && <span className="text-[12px] text-slate-600">{item.label}</span>}
            </div>
            {item.description && <p className="text-[11px] text-slate-500 mt-1">{item.description}</p>}
          </div>
          <Button size="sm" variant="ghost" disabled={busy} onClick={remove} className="h-7 text-[11px] text-red-500 hover:bg-red-50" data-testid={`sa-fr-delete-${item.key}`}>
            <Trash2 size={11} />
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {STATUS_DEFS.map(s => (
            <button
              key={s.value} type="button" onClick={() => setStatus(s.value)}
              className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold flex items-center gap-1 transition ${status === s.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 hover:bg-slate-50 text-slate-600'}`}
              data-testid={`sa-fr-status-${item.key}-${s.value}`}
            >
              <s.icon size={11} /> {s.label}
            </button>
          ))}
        </div>

        {showAllowlist && (
          <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid={`sa-fr-allowlist-${item.key}`}>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] text-slate-600 flex items-center gap-1.5"><Users2 size={11} /> Allowlist <Badge variant="outline" className="text-[9px] py-0 px-1.5 ml-1">{allowlist.length}</Badge></Label>
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search org…" className="h-7 text-xs w-44" />
            </div>
            <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
              {filteredOrgs.length === 0 && <p className="text-[11px] text-slate-400 text-center py-3">No matches</p>}
              {filteredOrgs.slice(0, 50).map(o => {
                const checked = allowlistSet.has(o.id);
                return (
                  <label key={o.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-[11px]" data-testid={`sa-fr-allow-${item.key}-${o.id}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(allowlist);
                        if (checked) next.delete(o.id); else next.add(o.id);
                        setAllowlist(Array.from(next));
                      }}
                    />
                    <span className="flex-1 truncate">{o.name}</span>
                    <span className="text-[9px] text-slate-400 font-mono">{o.id}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {dirty && (
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => { setStatus(item.status); setAllowlist(item.allowlist_org_ids || []); }} disabled={busy} className="h-7 text-[11px]">Discard</Button>
            <Button size="sm" onClick={save} disabled={busy} className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800" data-testid={`sa-fr-save-${item.key}`}>
              {busy ? <Loader2 size={11} className="animate-spin" /> : <><Check size={11} className="mr-1" /> Save</>}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
